<template>
  <div class="drop-down-select" :class="{ 'drop-down-select_active': model != null }" ref="rootEl">
    <p class="drop-down-select__header drop-down-select-header" @click="onHeaderClick">
      <span class="drop-down-select-header__title">
      <i class="fa-solid drop-down-select-header__icon" :class="icon" v-if="icon"/>
      {{ headerTitle }}
      </span>
      <i class="fa-solid fa-caret-down"/>
    </p>
    <Transition name="fade">
      <div class="drop-down-select__options" v-if="isOptionsVisible">
        <p
          class="drop-down-select-option"
          :class="{ 'drop-down-select-option_selected': isOptionSelected(option) }"
          v-for="option in options" :key="option.value"
          @click="onOptionClick(option)"
        >
          {{ option.label }}
        </p>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
export interface DropDownSelectOption {
  label: string,
  value: string,
}

export interface DropDownSelectProps {
  multiple?: boolean,
  options: DropDownSelectOption[],
  icon?: string,
  title: string,
}
const props = defineProps<DropDownSelectProps>();

const rootEl = ref<HTMLElement | null>();
const isOptionsVisible = ref(false);
const model = defineModel<string | string[] | null>({default: null, required: true,});


const headerTitle = computed<string>(() => {
  if(!model.value)
    return props.title;

  if(Array.isArray(model.value)) {
    if(model.value.length > 1)
      return `${model.value[0]} + ${model.value.length - 1}`;
    else if(model.value.length === 1)
      return model.value[0]!;
    else
      return props.title;
  }
  return model.value;
})

function isOptionSelected(option: DropDownSelectOption): boolean {
  return Array.isArray(model.value) ? model.value.includes(option.value) : model.value === option.value;
}

function onHeaderClick() {
  isOptionsVisible.value = !isOptionsVisible.value;
}

function onOptionClick(option: DropDownSelectOption) {
  let value = model.value;

  if (props.multiple) {
    if (Array.isArray(value)) {
      if (value.includes(option.value)) {
        value = value.filter(currentOption => currentOption != option.value)

        if(value?.length == 0)
          value = null;
      } else {
        value = [
          ...value,
          option.value
        ]
      }
    } else {
      value = [option.value];
    }
  } else {
    value = value === option.value ? null : option.value;
    isOptionsVisible.value = false;
  }

  model.value = value;
}

function windowClickEventListener(e: any) {
  if (isOptionsVisible.value && rootEl.value != null && !rootEl.value.contains(e.target)){
    isOptionsVisible.value = false;
  }
}

onMounted(() => {
  window.addEventListener('click', windowClickEventListener);
})
onUnmounted(() => {
  window.removeEventListener('click', windowClickEventListener);
})
</script>

<style lang="scss">
.drop-down-select {
  min-width: 256px;
  width: 100%;

  position: relative;

  //&_active,
  &:hover {
    .drop-down-select-header {
      color: white;
      background-color: #E43233;
      border-color: #E43233;
    }
  }

  &__options {
    position: absolute;
    top: 100%;
    width: 100%;
    z-index: 50;

    padding: 8px 0;

    background: #FFF;
    box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
  }
}

.drop-down-select-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  padding: 12px 20px;

  border: 2px solid rgba(33, 37, 41, 0.5333333333);
  border-radius: 8px;

  color: #212529;
  font-family: var(--font-family-main);
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  text-transform: uppercase;

  cursor: pointer;
  transition: all 150ms ease-out;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__icon {
    font-size: 16px;
  }
}

.drop-down-select-option {
  display: flex;
  align-items: center;
  gap: 12px;

  padding: 16px;

  color: #212529;
  font-family: var(--font-family-main);
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-transform: capitalize;

  cursor: pointer;
  transition: all 200ms ease-out;

  &:hover {
    background-color: #EFF1F5;
  }

  &::before {
    content: "☐";
  }

  &_selected {
    &::before {
      content: "☑";
      color: #E43233;
    }
  }
}
</style>